import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getDealDetail } from "@/lib/deals.functions";
import { getMarketCompsReportForDeal } from "@/lib/market-comps.functions";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute(
  "/_authed/deals/$dealId_/market-comps/$reportId",
)({
  head: () => ({
    meta: [
      { title: "Market Comps and Valuation Report: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MarketCompsReportPage,
});

/* Defensive readers: report_json is model assembled. */
type Dict = Record<string, unknown>;

function asDict(v: unknown): Dict | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Dict) : null;
}
function asDicts(v: unknown): Dict[] {
  return (Array.isArray(v) ? v : [])
    .map(asDict)
    .filter((d): d is Dict => !!d);
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}
function asNum(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function roundish(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
}
function moneyM(v: number | null): string {
  return v === null ? "" : "$" + roundish(v) + "M";
}
function shortDate(v: string | null): string {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

const SECTION_ORDER = [
  "executive_summary",
  "summary_stats",
  "similar_deals",
  "round_size_benchmarks",
  "valuation_multiples",
  "stepup_multiples",
  "time_between_rounds",
  "positioning",
] as const;

const FALLBACK_HEADING: Record<string, string> = {
  executive_summary: "Executive Summary",
  summary_stats: "Summary Stats",
  similar_deals: "Most Similar Deals",
  round_size_benchmarks: "Round Size Benchmarks",
  valuation_multiples: "Valuation Multiples",
  stepup_multiples: "Step-Up Multiples",
  time_between_rounds: "Time Between Rounds",
  positioning: "Positioning",
};


function Paragraphs({ body }: { body: string }) {
  const parts = body
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <>
      {parts.map((p, i) => (
        <p key={i} className="mt-2 text-sm text-brand-ink">
          {p}
        </p>
      ))}
    </>
  );
}

function LabelValueTable({ rows }: { rows: Dict[] }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[28rem] text-left text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-brand-line/60 align-top">
              <td className="w-1/2 py-2 pr-3 text-brand-ink">
                {asStr(r["label"]) ?? ""}
              </td>
              <td className="py-2 pr-3 text-brand-ink">
                <span>{asStr(r["value"]) ?? ""}</span>
                {asStr(r["note"]) && (
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {asStr(r["note"])}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryStatsTables({ tables }: { tables: Dict[] }) {
  return (
    <>
      {tables.map((t, ti) => {
        const rows = asDicts(t["rows"]);
        if (rows.length === 0) return null;
        return (
          <div key={ti} className="mt-4">
            <h3 className="text-sm font-semibold text-brand-ink">
              {asStr(t["title"]) ?? ""}
            </h3>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-brand-line">
                    <th className="py-2 pr-3 font-medium">Metric</th>
                    <th className="py-2 pr-3 font-medium">Median</th>
                    <th className="py-2 pr-3 font-medium">Low (25th pct)</th>
                    <th className="py-2 pr-3 font-medium">High (75th pct)</th>
                    <th className="py-2 pr-3 font-medium">Deals</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={i}
                      className="border-b border-brand-line/60 align-top"
                    >
                      <td className="py-2 pr-3 text-brand-ink">
                        {asStr(r["label"]) ?? ""}
                      </td>
                      <td className="py-2 pr-3 text-brand-ink">
                        {asStr(r["median"]) ?? ""}
                      </td>
                      <td className="py-2 pr-3 text-brand-ink">
                        {asStr(r["low"]) ?? ""}
                      </td>
                      <td className="py-2 pr-3 text-brand-ink">
                        {asStr(r["high"]) ?? ""}
                      </td>
                      <td className="py-2 pr-3 text-brand-ink">
                        {asNum(r["n"]) !== null ? String(asNum(r["n"])) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {asStr(t["population_line"]) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {asStr(t["population_line"])}
              </p>
            )}
            {asStr(t["coverage_line"]) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {asStr(t["coverage_line"])}
              </p>
            )}
            {ti === 0 && asStr(t["how_to_read"]) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {asStr(t["how_to_read"])}
              </p>
            )}
          </div>
        );
      })}
    </>
  );
}

const SIMILAR_HEADERS = [
  ["company", "Company"],
  ["date", "Date"],
  ["stage", "Stage"],
  ["deal_size", "Deal Size"],
  ["pre_money", "Pre-Money"],
  ["post_money", "Post-Money"],
  ["revenue", "Revenue"],
  ["investors", "Investors"],
] as const;

const WIDE_KEYS = [
  "description",
  "website",
  "company_type",
  "vertical",
  "city",
  "country",
  "deal_type",
  "pre_to_revenue",
  "lead_investors",
] as const;

/** Widened rows carry at least one of the newer fields. */
function isWideRow(r: Dict): boolean {
  return WIDE_KEYS.some((k) => k in r);
}

const WIDE_METRICS = [
  ["deal_size", "Deal Size"],
  ["pre_money", "Pre-Money"],
  ["post_money", "Post-Money"],
  ["revenue", "Revenue"],
  ["pre_to_revenue", "Pre/Rev"],
] as const;

function hqLine(r: Dict): string {
  const city = asStr(r["city"]);
  const state = asStr(r["state_province"]);
  const country = asStr(r["country"]);
  const second = state ?? country;
  return [city, second].filter(Boolean).join(", ");
}

function siteHref(v: string | null): string | null {
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : "https://" + v;
}

function SimilarDealsWideTable({ rows }: { rows: Dict[] }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-brand-line">
            <th className="py-2 pr-3 font-medium">Company</th>
            {WIDE_METRICS.map(([k, label]) => (
              <th key={k} className="py-2 pl-3 text-right font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const company = asStr(r["company"]) ?? "";
            const href = siteHref(asStr(r["website"]));
            const hq = hqLine(r);
            const meta = [
              hq,
              [asStr(r["company_type"]), asStr(r["vertical"])]
                .filter(Boolean)
                .join(", "),
              asStr(r["stage"]),
              asStr(r["deal_type"]),
              shortDate(asStr(r["date"])),
            ].filter((s) => s && s.length > 0);
            const description = asStr(r["description"]);
            const leads = asStr(r["lead_investors"]);
            const others = asStr(r["other_investors"]);
            const investorLine = [
              leads
                ? leads
                    .split(", ")
                    .map((n) => n + " (lead)")
                    .join(", ")
                : null,
              others,
            ]
              .filter(Boolean)
              .join(", ");
            return (
              <tr key={i} className="border-b border-brand-line/60 align-top">
                <td className="py-2 pr-3 text-brand-ink">
                  <div className="font-medium">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2"
                      >
                        {company}
                      </a>
                    ) : (
                      company
                    )}
                    {meta.length > 0 && (
                      <span className="font-normal text-brand-muted">
                        {" "}
                        {meta.join(" · ")}
                      </span>
                    )}
                  </div>
                  {description && (
                    <div className="truncate text-xs text-brand-muted">
                      {description}
                    </div>
                  )}
                  {investorLine && (
                    <div className="text-xs text-brand-muted">
                      {investorLine}
                    </div>
                  )}
                </td>
                {WIDE_METRICS.map(([k]) => (
                  <td
                    key={k}
                    className="whitespace-nowrap py-2 pl-3 text-right text-brand-ink"
                  >
                    {asStr(r[k]) ?? ""}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SimilarDealsTable({ rows }: { rows: Dict[] }) {
  if (rows.some(isWideRow)) return <SimilarDealsWideTable rows={rows} />;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[52rem] text-left text-sm">
        <thead>
          <tr className="border-b border-brand-line">
            {SIMILAR_HEADERS.map(([k, label]) => (
              <th key={k} className="py-2 pr-3 font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-brand-line/60 align-top">
              {SIMILAR_HEADERS.map(([k]) => (
                <td
                  key={k}
                  className={
                    "py-2 pr-3 text-brand-ink " +
                    (k === "investors" ? "whitespace-normal" : "whitespace-nowrap")
                  }
                >
                  {k === "date"
                    ? shortDate(asStr(r["date"]))
                    : (asStr(r[k]) ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImpliedValuationBlock({ implied }: { implied: Dict }) {
  const low = asNum(implied["low"]);
  const median = asNum(implied["median"]);
  const high = asNum(implied["high"]);
  const revenue = asNum(implied["revenue_musd"]);
  const basisN = asNum(implied["basis_n"]);
  const target = asNum(implied["target_premoney_musd"]);
  if (low === null || median === null || high === null || revenue === null)
    return null;
  return (
    <div className="mt-4 rounded-md border border-brand-line bg-brand-line/20 p-3">
      <p className="text-sm font-semibold text-brand-ink">
        Implied pre-money valuation range
      </p>
      <p className="mt-1 text-sm text-brand-ink">
        {moneyM(low)} to {moneyM(high)}, centered on {moneyM(median)}, from your
        revenue of {moneyM(revenue)} and the segment&rsquo;s pre-money to revenue
        multiples (based on {basisN ?? 0} deals).
        {target !== null ? ` Your target: ${moneyM(target)}.` : ""}
      </p>
    </div>
  );
}

function MarketCompsReportPage() {
  const { dealId, reportId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fetchReport = useServerFn(getMarketCompsReportForDeal);
  const fetchDeal = useServerFn(getDealDetail);

  const reportQuery = useQuery({
    queryKey: ["market-comps-report", dealId, reportId],
    queryFn: () => fetchReport({ data: { dealId, reportId } }),
    enabled: !!user,
  });

  const dealQuery = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDeal({ data: { dealId } }),
    enabled: !!user,
  });

  const row = (reportQuery.data?.report ?? null) as Dict | null;
  const status = asStr(row?.["report_status"]);

  useEffect(() => {
    if (status === "processing") {
      navigate({ to: "/deals/$dealId", params: { dealId } });
    }
  }, [status, dealId, navigate]);

  useEffect(() => {
    document.body.classList.add("model-review-print");
    return () => {
      document.body.classList.remove("model-review-print");
    };
  }, []);

  if (reportQuery.isLoading || dealQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-muted-foreground">
        Loading the report...
      </div>
    );
  }

  if (reportQuery.isError || !row) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <p className="text-sm text-brand-ink">This report is not available.</p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  const dealName =
    asStr((dealQuery.data as { deal?: { name?: string } } | undefined)?.deal?.name) ??
    "Deal";
  const pageTitle = `${dealName} Market Comps and Valuation Report`;

  const handleDownloadPdf = () => {
    const originalTitle = document.title;
    document.title = pageTitle;
    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    setTimeout(() => {
      if (document.title === pageTitle) {
        document.title = originalTitle;
        window.removeEventListener("afterprint", restore);
      }
    }, 1000);
    window.print();
  };

  if (status === "failed") {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">
          <span className="block">{dealName}</span>
          <span className="block">Market Comps and Valuation Report</span>
        </h1>
        <p className="text-sm text-brand-ink">
          This report did not finish. Any credits charged were returned.
        </p>
        {asStr(row["failed_reason"]) && (
          <p className="text-xs text-muted-foreground">
            Reason: {asStr(row["failed_reason"])}
          </p>
        )}
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  if (status === "processing") return null;

  const report = asDict(row["report_json"]) ?? {};
  const narrative = asDict(report["narrative"]) ?? {};
  const deal = asDict(report["deal"]) ?? {};
  const disclaimer = asStr(report["disclaimer"]);
  const generatedAt = asStr(report["generated_at"]);
  const summaryStatsTables = asDicts(report["summary_stats_tables"]);
  const segmentBasisLine = asStr(report["segment_basis_line"]);
  const similarDealsRows = asDicts(report["similar_deals_table"]);
  const impliedValuation = asDict(report["implied_valuation"]);

  const sections = SECTION_ORDER.map((key) => {
    const sec = asDict(narrative[key]);
    if (!sec) return null;
    const body = asStr(sec["body"]) ?? "";
    const table = asDicts(sec["table"]);
    const extras =
      (key === "summary_stats" && summaryStatsTables.length > 0) ||
      (key === "similar_deals" && similarDealsRows.length > 0);
    if (!body && table.length === 0 && !extras) return null;
    return {
      key,
      heading: asStr(sec["heading"]) ?? FALLBACK_HEADING[key] ?? key,
      body,
      table,
    };
  }).filter((s): s is NonNullable<typeof s> => !!s);

  return (
    <div className="model-review-report mx-auto max-w-4xl space-y-5 px-4 py-8">
      <style>{`
        @media print {
          @page { margin: 0.75in; }
          body.model-review-print {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.model-review-print header.sticky { display: none !important; }
          .model-review-report .no-print { display: none !important; }
          .model-review-report {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .model-review-report section,
          .model-review-report .rounded-lg {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0.5rem 0 !important;
            margin: 1rem 0 !important;
          }
          .model-review-report h1,
          .model-review-report h2,
          .model-review-report h3 {
            color: #000 !important;
            break-after: avoid;
          }
          .model-review-report .font-serif { color: #000 !important; }
          .model-review-report p,
          .model-review-report li,
          .model-review-report td,
          .model-review-report th { color: #000 !important; }
          .model-review-report .text-muted-foreground,
          .model-review-report .text-brand-muted { color: #333 !important; }
          .model-review-report .overflow-x-auto {
            overflow: visible !important;
            width: 100% !important;
          }
          .model-review-report table {
            width: 100% !important;
            min-width: 0 !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
          }
          .model-review-report thead { display: table-row-group !important; }
          .model-review-report tr {
            border-top: 1px solid #ccc !important;
            border-bottom: 1px solid #ccc !important;
            page-break-inside: auto !important;
          }
          .model-review-report td,
          .model-review-report th {
            padding: 0.35rem 0.5rem 0.35rem 0 !important;
            vertical-align: top !important;
            border: none !important;
          }
          .model-review-report .border-brand-line { border-color: #ccc !important; }
        }
      `}</style>

      <div className="no-print space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="reports" />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-brand-ink">
            <span className="block">{dealName}</span>
            <span className="block">Market Comps and Valuation Report</span>
          </h1>
          {(asStr(deal["stage"]) || asStr(deal["sector"])) && (
            <p className="mt-1 text-sm text-brand-muted">
              {[asStr(deal["stage"]), asStr(deal["sector"])]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {generatedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Prepared {new Date(generatedAt).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 no-print">
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Download PDF
          </button>
          <Link
            to="/deals/$dealId"
            params={{ dealId }}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Back to the deal
          </Link>
        </div>
      </div>

      {sections.map((s, i) => (
        <section
          key={s.key}
          className="rounded-lg border border-brand-line bg-brand-paper p-5"
        >
          <h2 className="font-serif text-lg text-brand-ink">
            {i + 1}. {s.heading}
          </h2>
          <div className="mt-3">
            {s.body && <Paragraphs body={s.body} />}
            {s.table.length > 0 && <LabelValueTable rows={s.table} />}
            {s.key === "executive_summary" && impliedValuation && (
              <ImpliedValuationBlock implied={impliedValuation} />
            )}
            {s.key === "summary_stats" && segmentBasisLine && (
              <p className="mt-2 text-xs text-muted-foreground">
                {segmentBasisLine}
              </p>
            )}
            {s.key === "summary_stats" && summaryStatsTables.length > 0 && (
              <SummaryStatsTables tables={summaryStatsTables} />
            )}
            {s.key === "similar_deals" && similarDealsRows.length > 0 && (
              <SimilarDealsTable rows={similarDealsRows} />
            )}
          </div>
        </section>
      ))}

      {sections.length === 0 && (
        <p className="text-sm text-brand-muted">
          This report has no content to display.
        </p>
      )}

      {disclaimer && (
        <p className="pt-2 text-xs text-muted-foreground">{disclaimer}</p>
      )}
    </div>
  );
}
